import { Box, Button, Grid, Typography } from '@material-ui/core'
import firebase from 'firebase/compat/app'
import 'firebase/compat/firestore'
import type { ChangeEvent, FC } from 'react'
import { useEffect, useState } from 'react'
import { BeatLoader } from 'react-spinners'
// @ts-ignore react-select has no exported @types/ module
import Select from 'react-select'
import { toast } from 'react-toastify'
import type { BusinessType } from '../../../../src/types/business'
import type { JobType, JobTypeWithDistance } from '../../../../src/types/jobs'
import { errorToast } from '../../helpers/toast'
import { updateStafferRating } from '../../staffers/api/firestore/staffer.legacy'
import muiButtonStyles from '../mui/Buttons.module.css'
import ModalHeader from '../mui/ModalHeader'
import TextField from '../textInputField/TextInputField'
import styles from './EditShiftRating.module.css'
import React from 'react'

type RatingOption = {
  value?: number | null
  label: string | number
}

type Props = {
  currentRating?: number | null
  currentComment?: string
  initialRating?: number | null
  initialComment?: string
  stafferId: string
  businessId: string
  isHocProcessing?: boolean
  isStafferApproval?: boolean
  shiftId: string
  timeStart?: firebase.firestore.Timestamp
  timeEnd?: firebase.firestore.Timestamp
  onClose?: () => void
  onGoBack?: () => void
  onRatingChange?: (rating?: number | null, comment?: string, canSaveRating?: boolean) => any
  isNewRating?: boolean
  approveShift?: () => Promise<boolean>
  job: JobType | JobTypeWithDistance
  business?: BusinessType
  onResolveCallback?: VoidFunction
  containerPadding?: number
  resolveShift?: boolean
}

const COMMENT_REQUIRED_ON = 3

const EditShiftRating: FC<Props> = ({
  currentRating,
  currentComment,
  initialRating,
  initialComment,
  stafferId,
  businessId,
  isHocProcessing,
  isStafferApproval,
  shiftId,
  timeStart,
  timeEnd,
  onClose,
  onGoBack,
  onRatingChange,
  isNewRating,
  approveShift,
  job,
  business,
  onResolveCallback,
  containerPadding = 0,
  resolveShift = false,
}) => {
  const getRatingOptions = () => {
    if (!onResolveCallback) {
      return [
        { value: 1, label: '1' },
        { value: 2, label: '2' },
        { value: 3, label: '3' },
        { value: 4, label: '4' },
        { value: 5, label: '5' },
      ]
    }
    return [
      { value: 1, label: '1' },
      { value: 2, label: '2' },
      { value: 3, label: '3' },
      { value: 4, label: '4' },
      { value: 5, label: '5' },
      { value: 0, label: 'No rating' },
    ]
  }
    const ratingOptions = getRatingOptions()
    const defaultRating = ratingOptions[ratingOptions.length - 1]
    const [updatedRating, setUpdatedRating] = useState<RatingOption>({
      value: initialRating || currentRating || defaultRating.value,
      label: initialRating || currentRating || defaultRating.label,
    })
    const [updatedComment, setUpdatedComment] = useState(currentComment || initialComment || '')
    const [isProcessing, setIsProcessing] = useState(false)
    const [canSaveRating, setCanSaveRating] = useState((currentRating !== undefined && currentRating !== null) || (initialRating !== null && initialRating !== undefined) || defaultRating.value === 0)
    
    // Keep track of the previous comment from props to avoid unnecessary updates
    const prevCommentRef = React.useRef(currentComment)

  // Update the dropdown when currentRating changes (from parent)
  useEffect(() => {
    // Only update if the values don't match to avoid unnecessary rerenders
    if (currentRating !== updatedRating.value && !resolveShift) {
      const selectedOption = ratingOptions.find(option => option.value === currentRating) || 
        {value: currentRating, label: currentRating?.toString() || 'Unrated'};
      setUpdatedRating(selectedOption);
    }
  }, [currentRating, ratingOptions, resolveShift]);

  // Sync comment from props only when props actually change
  useEffect(() => {
    if (currentComment !== prevCommentRef.current && !resolveShift) {
      prevCommentRef.current = currentComment;
      setUpdatedComment(currentComment || '');
    }
  }, [currentComment, resolveShift]);

  const handleCommentChange = (e: ChangeEvent<HTMLInputElement>) => {
    const comment = e.target.value
    let canSaveRating = false
    if (updatedRating.value) {
      if (updatedRating.value <= COMMENT_REQUIRED_ON) {
        canSaveRating = !!comment
      } else {
        canSaveRating = true
      }
    }
    setUpdatedComment(comment)
    setCanSaveRating(canSaveRating)
    
    // Notify parent component of comment change too
    if (onRatingChange) {
      onRatingChange(updatedRating.value, comment, canSaveRating)
    }
  }

  // Handle dropdown menu
  const handleRatingChange = (selectedOption: RatingOption) => {
    // Always update the UI with the selected option, even if it requires a comment
    setUpdatedRating(selectedOption)

    let canSaveRating = false
    if (selectedOption && selectedOption.value !== undefined && selectedOption.value !== null) {
      if (selectedOption.value <= COMMENT_REQUIRED_ON && selectedOption.value !== 0) {
        canSaveRating = !!updatedComment
      } else {
        canSaveRating = selectedOption.value !== undefined && selectedOption.value !== null
      }
    }
    
    setCanSaveRating(canSaveRating)
    
    // Rating changes should be immediate - now passing the actual selectedOption regardless of canSaveRating
    if (onRatingChange) {
      onRatingChange(selectedOption.value, updatedComment || '', canSaveRating)
    }
  }

  // Save changes method
  const onSaveChangesHandler = async () => {
    if (approveShift) {
      await approveShift()
    }

    const successMessage = approveShift
      ? `Staffer was successfully approved${canSaveRating || updatedRating.value !== 0 ? ' and rated' : ''}`
      : "Staffer's rating was successfully changed"

    try {
      setIsProcessing(true)
      if (canSaveRating && updatedRating.value) {
        if (updatedRating.value <= COMMENT_REQUIRED_ON && !updatedComment) {
          throw Error(`Ratings of ${COMMENT_REQUIRED_ON} or less require a comment.`)
        }
        await updateStafferRating(
          stafferId,
          businessId,
          job.id,
          shiftId,
          timeStart,
          timeEnd,
          updatedRating.value,
          updatedComment || '',
          job.businessName,
          job.jobType
        )
        toast.success(successMessage)
        approveShift && onResolveCallback && onResolveCallback()
      } else {
        toast.success(successMessage)
        approveShift && onResolveCallback && onResolveCallback()
      }
    } catch (error) {
      console.error(error)
      errorToast((error as Error).message)
    } finally {
      setIsProcessing(false)
      if (onClose) {
        onClose()
      }
    }
  }

  // Delete comment/rating method
  const onDeleteHandler = async (fieldToDelete: string) => {

    try {
      const selectedRatingsRef = await firebase
        .firestore()
        .collection('staffers')
        .doc(stafferId)
        .collection('ratings')
        .doc(businessId)

      const selectedRatingsDoc = await selectedRatingsRef.get()
      const selectedRatingsData = selectedRatingsDoc.data()

      let updatedRatings = []

      if (selectedRatingsData) {
        // Store and update current ratings
        if (fieldToDelete === 'comment') {
          updatedRatings = selectedRatingsData.ratings.map((rating: Record<string, string>) => {
            if (rating.shiftId === shiftId) {
              return {
                ...rating,
                comment: '',
              }
            }
            return rating
          })
        } else if (fieldToDelete === 'rating') {
          updatedRatings = selectedRatingsData.ratings.map((rating: Record<string, string>) => {
            if (rating.shiftId === shiftId) {
              return {
                ...rating,
                value: null,
              }
            }
            return rating
          })
        }
      }

      try {
        setIsProcessing(true)
        await selectedRatingsRef.update({
          ratings: updatedRatings,
        })
        if (fieldToDelete === 'rating') {
          setUpdatedRating({
            value: null,
            label: 'Unrated',
          })
        }
        if (fieldToDelete === 'comment') {
          setUpdatedComment('')
        }
        toast.success(`Shift ${fieldToDelete} was deleted successfully.`)
      } catch (error) {
        errorToast((error as Error).message)
        console.warn(error)
      } finally {
        setIsProcessing(false)
        if (onClose) {
          onClose()
        }
      }
    } catch (error) {
      console.warn(error)
    }
  }

    if (isHocProcessing) {
      return null
    }

    if (isProcessing || !business) {
      return (
        <div className={styles.loaderWrapper}>
          <BeatLoader />
        </div>
      )
    }

    return (
      <div className={styles.modal}>
        {!isStafferApproval && isNewRating && (
          <ModalHeader close={onClose} goBack={onGoBack}>
            Add shift rating/comment
          </ModalHeader>
        )}
        <div className={styles.modalContent} style={{ padding: containerPadding }}>
          {!business.ratingDisabled && (
            <>
                <div className={styles.modalContentCol}>
                  <span className={styles.sectionTitle}>{isNewRating ? 'Rating:' : 'Edit rating:'}</span>
                  <Select
                    menuPortalTarget={global.document.querySelector('body')}
                    styles={{
                      menuPortal: (base: Record<string, string>) => ({ ...base, zIndex: 2001 }),
                      container: (base: Record<string, string>) => ({ ...base, width: '100%' }),
                      control: (base: Record<string, string>) => ({ ...base, width: '100%', borderRadius: 10 }),
                      indicatorSeparator: (base: Record<string, string>) => ({ ...base, display: 'none' })
                  }}
                    value={updatedRating}
                    options={ratingOptions}
                    onChange={handleRatingChange}
                  />
                  <span className={styles.sectionSubtitle}>Your rating will not be visible to the staffer</span>
                  {initialRating && initialRating === updatedRating.value && (
                    <Button
                      variant="contained"
                      className={muiButtonStyles.danger}
                      onClick={() => onDeleteHandler('rating')}
                    >
                      Delete Rating
                    </Button>
                  )}
                </div>
                <div className={styles.modalContentCol}>
                  <span className={styles.sectionTitle}>{isNewRating ? 'Comment:' : 'Edit comment:'}</span>
                  <TextField
                    type="text"
                    placeholder='Write what you were or were not satisfied with...'
                    inputProps={{
                      style: {
                        padding: 10
                      }
                    }}
                    name="comment"
                    multiline
                    rows={5}
                    onChange={handleCommentChange}
                    value={updatedComment}
                    disableUnderline
                    className={styles.textField}
                  />
                  <span className={styles.sectionSubtitle}>This is only visible to other businesses</span>
                  {initialComment && initialComment === updatedComment && (
                    <Button
                      variant="contained"
                      className={muiButtonStyles.danger}
                      onClick={() => onDeleteHandler('comment')}
                    >
                      Delete Comment
                    </Button>
                  )}
                </div>
            </>
          )}
          {
            // || 5 to ensure the condition fails if value is not yet defined
            !canSaveRating && (updatedRating.value || 5) <= 3 && (
              <Grid item xs={12}>
                <Typography color="error" align="center">
                  {`Ratings of ${COMMENT_REQUIRED_ON} or less require a comment`}
                </Typography>
              </Grid>
            )
          }
        </div>
        {!onRatingChange && (
          <Box my={4} textAlign="center">
            <Button className={muiButtonStyles.default} color="default" variant="contained" onClick={onClose}>
              Cancel
            </Button>
            <Button
              className={muiButtonStyles.default}
              color="primary"
              onClick={onSaveChangesHandler}
              variant="contained"
              disabled={!canSaveRating}
            >
              {approveShift ? 'Finish approving' : 'Save rating'}
            </Button>
          </Box>
        )}
      </div>
    )
}

export default EditShiftRating
